import {
  CONFIGURE_API_RATE_LIMIT_AGGREGATE_BY_OPTIONS,
  CONFIGURE_API_RATE_LIMIT_LIMIT_BY_OPTIONS
} from '~/scenes/Build/scenes/Pipelines/components/ConfigureApiModal/configureApiRateLimit';
import { RATE_LIMIT_INTERVAL_VALUES } from '~/scenes/Build/scenes/Pipelines/components/ConfigureApiModal/configureApiRateLimitOptions';

const [AGGREGATE_LABEL_CONSUMER, AGGREGATE_LABEL_CREDENTIAL] =
  CONFIGURE_API_RATE_LIMIT_AGGREGATE_BY_OPTIONS;

/**
 * Modal persists aggregateBy labels; canvas persists rateLimitAggregateBy enums.
 * When they diverge, resolve using asymmetric rules for the two known stale shapes.
 */
function reconcileAggregateByConflict(
  spec: Record<string, unknown>
): Record<string, unknown> {
  const aggLabel =
    typeof spec.aggregateBy === 'string' ? spec.aggregateBy.trim() : '';
  const rlAggRaw =
    typeof spec.rateLimitAggregateBy === 'string'
      ? spec.rateLimitAggregateBy.trim().toLowerCase()
      : '';

  const aggIsCredentialLabel = aggLabel === AGGREGATE_LABEL_CREDENTIAL;
  const aggIsConsumerLabel =
    aggLabel.toLowerCase() === AGGREGATE_LABEL_CONSUMER.toLowerCase();

  if (aggIsCredentialLabel && rlAggRaw === 'consumer') {
    return { ...spec, rateLimitAggregateBy: 'credential' };
  }
  if (aggIsConsumerLabel && rlAggRaw === 'credential') {
    return {
      ...spec,
      aggregateBy: AGGREGATE_LABEL_CREDENTIAL,
      rateLimitAggregateBy: 'credential'
    };
  }
  return spec;
}

/** Connector / graphql duplicate keys use lowercase enums; avoid broad /credential/ regex elsewhere in the codebase. */
function mapAggregateByLabelToRateLimitEnum(label: string): string | undefined {
  const t = label.trim();
  if (t === AGGREGATE_LABEL_CREDENTIAL) return 'credential';
  if (t.toLowerCase() === AGGREGATE_LABEL_CONSUMER.toLowerCase())
    return 'consumer';
  if (t === 'credential') return 'credential';
  if (t === 'consumer') return 'consumer';
  return undefined;
}

function syncLegacyRateLimitKeys(
  spec: Record<string, unknown>
): Record<string, unknown> {
  let draft = { ...spec };

  let aggLabel =
    typeof draft.aggregateBy === 'string' ? draft.aggregateBy.trim() : '';
  const rlAggEarly =
    typeof draft.rateLimitAggregateBy === 'string'
      ? draft.rateLimitAggregateBy.trim().toLowerCase()
      : '';

  if (!aggLabel && (rlAggEarly === 'credential' || rlAggEarly === 'consumer')) {
    draft = {
      ...draft,
      aggregateBy:
        rlAggEarly === 'credential'
          ? AGGREGATE_LABEL_CREDENTIAL
          : AGGREGATE_LABEL_CONSUMER
    };
    aggLabel =
      typeof draft.aggregateBy === 'string' ? draft.aggregateBy.trim() : '';
  }

  draft = reconcileAggregateByConflict(draft);

  aggLabel =
    typeof draft.aggregateBy === 'string' ? draft.aggregateBy.trim() : '';
  if (aggLabel) {
    const rlAgg = mapAggregateByLabelToRateLimitEnum(aggLabel);
    if (rlAgg !== undefined) {
      draft = { ...draft, rateLimitAggregateBy: rlAgg };
    }
  }

  const limTrimmed =
    typeof draft.limitBy === 'string' ? draft.limitBy.trim() : '';
  const limitMatchesConfigureApi =
    CONFIGURE_API_RATE_LIMIT_LIMIT_BY_OPTIONS.some(
      opt => opt.toLowerCase() === limTrimmed.toLowerCase()
    );
  if (limitMatchesConfigureApi) {
    draft = { ...draft, rateLimitBy: 'pipeline' };
  }
  return draft;
}

const INTERVAL_ALIASES: Record<string, string> = {
  seconds: 'second',
  minutes: 'minute',
  hours: 'hour',
  days: 'day',
  months: 'month'
};

function normalizeIntervalValue(raw: unknown): string {
  if (typeof raw !== 'string') return 'second';
  const v = raw.trim().toLowerCase();
  if (INTERVAL_ALIASES[v]) return INTERVAL_ALIASES[v];
  return (RATE_LIMIT_INTERVAL_VALUES as readonly string[]).includes(v)
    ? v
    : 'second';
}

function coerceLimit(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || Number.isNaN(n) || n < 1) return 0;
  return Math.trunc(n);
}

function isPlainItem(item: unknown): item is Record<string, unknown> {
  return Boolean(item) && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Backend may persist rate-limit arrays as JSON strings instead of native arrays
 * (observed on `triggerSpec.rateLimitOptions` after canvas saves). Accept both shapes.
 */
function coerceArray(raw: unknown): unknown[] | undefined {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '[]') return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Modal persists `triggerSpec.options` as `[{ intervalValue, interval, limit }]`;
 * canvas (FantaForm) reads from `triggerSpec.rateLimitOptions` as `[{ interval, limit }]`
 * where the implicit `intervalValue` is the row index + 1.
 * Mirror one shape into the other when the target key is absent, so each UI
 * renders what the other saved without backend coordination. Also normalize the
 * existing side into a clean array shape (backend may store it as a JSON string).
 */
function syncRateLimitOptionsArray(
  spec: Record<string, unknown>
): Record<string, unknown> {
  const modalOpts = coerceArray(spec.options);
  const canvasOpts = coerceArray(spec.rateLimitOptions);

  if (canvasOpts == null && modalOpts != null) {
    const sorted = [...modalOpts.filter(isPlainItem)].sort((a, b) => {
      const ai = Number((a as { intervalValue?: unknown }).intervalValue);
      const bi = Number((b as { intervalValue?: unknown }).intervalValue);
      return (
        (Number.isFinite(ai) && ai >= 1 ? ai : 1) -
        (Number.isFinite(bi) && bi >= 1 ? bi : 1)
      );
    });
    const cleanModal = sorted.map((o, idx) => ({
      intervalValue: idx + 1,
      interval: normalizeIntervalValue(o.interval),
      limit: coerceLimit(o.limit)
    }));
    return {
      ...spec,
      options: cleanModal,
      rateLimitOptions: cleanModal.map(({ interval, limit }) => ({
        interval,
        limit
      }))
    };
  }

  if (modalOpts == null && canvasOpts != null) {
    const cleanCanvas = canvasOpts.filter(isPlainItem).map(o => ({
      interval: normalizeIntervalValue(o.interval),
      limit: coerceLimit(o.limit)
    }));
    return {
      ...spec,
      rateLimitOptions: cleanCanvas,
      options: cleanCanvas.map((o, idx) => ({
        intervalValue: idx + 1,
        interval: o.interval,
        limit: o.limit
      }))
    };
  }

  return spec;
}

/**
 * GraphQL exposes pipeline.triggerSpec as JSON. Some backends serialize REST
 * rate-limit fields as snake_case; canvas (FantaForm) and Configure API use camelCase.
 * Hydrate camelCase from snake_case when absent so values survive fetch → UI round-trip.
 * Parse JSON strings before normalizing.
 *
 * Backends may duplicate settings under connector keys (`rateLimitBy`, `rateLimitAggregateBy`)
 * while Configure API persists labels (`limitBy`, `aggregateBy`). When both disagree, the canvas
 * can show stale connector enums — `syncLegacyRateLimitKeys` reconciles and forwards labels.
 */
export function normalizeTriggerSpecFromApi(
  raw: unknown
): Record<string, unknown> {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  let spec = { ...(parsed as Record<string, unknown>) };

  const nonEmptyString = (v: unknown): string | undefined => {
    if (typeof v !== 'string') return undefined;
    const t = v.trim();
    return t !== '' ? t : undefined;
  };

  if (spec.aggregateBy == null) {
    const fromSnake = nonEmptyString(spec.aggregate_by);
    if (fromSnake !== undefined) {
      spec = { ...spec, aggregateBy: fromSnake };
    }
  }

  if (spec.limitBy == null) {
    const fromSnake = nonEmptyString(spec.limit_by);
    if (fromSnake !== undefined) {
      spec = { ...spec, limitBy: fromSnake };
    }
  }

  spec = syncLegacyRateLimitKeys(spec);
  spec = syncRateLimitOptionsArray(spec);

  return spec;
}
